import request from "../../utils/request";
import { createAsyncThunk } from "@reduxjs/toolkit";

const getAllDrivers = createAsyncThunk(
    "allDrivers/getAllDrivers",
    async ({limit, status}, { rejectWithValue }) => {

        let url = `/drivers?status=${status}`
        if (limit){
            url = `/drivers?limit=${limit}&status=${status}`
        }
        try {
            const { data } = await request.get(url, {
                withCredentials: true,
            });

            return data;
        } catch (err) {
            return rejectWithValue(err.response.data);
        }
    }
)

// Rich admin driver list. Server returns enriched records with rating,
// compliance, market, orders aggregated per driver.
const getAdminDrivers = createAsyncThunk(
    "adminDrivers/getAdminDrivers",
    async (params = {}, { rejectWithValue }) => {
        try {
            const qs = new URLSearchParams();
            Object.entries(params).forEach(([k, v]) => {
                if (v !== undefined && v !== null && v !== "") qs.set(k, v);
            });
            const { data } = await request.get(`/admin/drivers?${qs.toString()}`, {
                withCredentials: true,
            });
            return data;
        } catch (err) {
            return rejectWithValue(err.response?.data || { message: err.message });
        }
    }
);

const getAdminDriverDetail = createAsyncThunk(
    "adminDrivers/getAdminDriverDetail",
    async (did, { rejectWithValue }) => {
        try {
            const { data } = await request.get(`/admin/drivers/${did}`, {
                withCredentials: true,
            });
            return data;
        } catch (err) {
            return rejectWithValue(err.response?.data || { message: err.message });
        }
    }
);

const updateDriverStatus = createAsyncThunk(
    "adminDrivers/updateDriverStatus",
    async ({ did, isActive, reason }, { rejectWithValue }) => {
        try {
            const { data } = await request.patch(
                `/admin/drivers/${did}/status`,
                { isActive, reason },
                { withCredentials: true }
            );
            return data;
        } catch (err) {
            return rejectWithValue(err.response?.data || { message: err.message });
        }
    }
);

// Admin override for a driver's Checkr background-check status. The Checkr
// webhook sometimes fails to update a driver to "clear" after vendor
// review, leaving onboarded drivers stuck ineligible. This lets ops push
// the status manually with an audit-trail reason.
const updateDriverCheckrStatus = createAsyncThunk(
    "adminDrivers/updateDriverCheckrStatus",
    async ({ did, status, reason }, { rejectWithValue }) => {
        try {
            const { data } = await request.patch(
                `/admin/drivers/${did}/checkr-status`,
                { status, reason },
                { withCredentials: true }
            );
            return data;
        } catch (err) {
            return rejectWithValue(err.response?.data || { message: err.message });
        }
    }
);

const updateDriverRating = createAsyncThunk(
    "adminDrivers/updateDriverRating",
    async ({ did, rating, reason }, { rejectWithValue }) => {
        try {
            const { data } = await request.patch(
                `/admin/drivers/${did}/rating`,
                { rating, reason },
                { withCredentials: true }
            );
            return data;
        } catch (err) {
            return rejectWithValue(err.response?.data || { message: err.message });
        }
    }
);

const getDriverAdjustments = createAsyncThunk(
    "adminDrivers/getDriverAdjustments",
    async (did, { rejectWithValue }) => {
        try {
            const { data } = await request.get(`/admin/drivers/${did}/adjustments`, {
                withCredentials: true,
            });
            return data;
        } catch (err) {
            return rejectWithValue(err.response?.data || { message: err.message });
        }
    }
);

const createDriverAdjustment = createAsyncThunk(
    "adminDrivers/createDriverAdjustment",
    async ({ did, type, amount, reason, orderId }, { rejectWithValue }) => {
        try {
            const { data } = await request.post(
                `/admin/drivers/${did}/adjustments`,
                { type, amount, reason, orderId },
                { withCredentials: true }
            );
            return data;
        } catch (err) {
            return rejectWithValue(err.response?.data || { message: err.message });
        }
    }
);

const reviewDriverCompliance = createAsyncThunk(
    "adminDrivers/reviewDriverCompliance",
    async ({ did, doc, status, rejectionReason }, { rejectWithValue }) => {
        try {
            const { data } = await request.patch(
                `/admin/drivers/${did}/compliance/${doc}`,
                { status, rejectionReason },
                { withCredentials: true }
            );
            return data;
        } catch (err) {
            return rejectWithValue(err.response?.data || { message: err.message });
        }
    }
);

const getDriverDetails = createAsyncThunk(
    "driverDetails/getDriverDetails",
    async (did, { rejectWithValue }) => {
        try {
            const { data } = await request.get(`/drivers/${did}`, {
                withCredentials: true,
            });

            return data;
        } catch (err) {
            return rejectWithValue(err.response.data);
        }
    }
)

const getDriverOrders = createAsyncThunk(
    "driverOrders/getDriverOrders",
    async (did, { rejectWithValue }) => {
        try {
            const { data } = await request.get(`/orders/driver/${did}`, {
                withCredentials: true,
            });
            return data;
        } catch (err) {
            return rejectWithValue(err.response.data);
        }
    }
);

const deleteDriverAction = createAsyncThunk(
    "deletedriver/deleteDriverAction",
    async (did, { rejectWithValue }) => {
        try {
            const { data } = await request.delete(`/drivers/${did}`, {
                withCredentials: true,
            });

            return data;
        } catch (err) {
            return rejectWithValue(err.response.data);
        }
    }
);

const getDriverEarnings = createAsyncThunk(
    "driverEarnings/getDriverEarnings",
    async (did, { rejectWithValue }) => {

        let url = `/orders/graph/${did}?type=driver&duration=30&status=completed`;

        try {
            const { data } = await request.get(url, {
                withCredentials: true,
            });

            return data;
        } catch (err) {
            return rejectWithValue(err.response.data);
        }
    }
);

const getDriverPendingEarnings = createAsyncThunk(
    "driverPendingEarnings/getDriverPendingEarnings",
    async (did, { rejectWithValue }) => {

        let url = `/orders/graph/${did}?type=driver&duration=30&status=pending,requested,picked`;

        try {
            const { data } = await request.get(url, {
                withCredentials: true,
            });

            return data;
        } catch (err) {
            return rejectWithValue(err.response.data);
        }
    }
);

export {
    getAllDrivers,
    getDriverDetails,
    getDriverOrders,
    deleteDriverAction,
    getDriverEarnings,
    getDriverPendingEarnings,
    getAdminDrivers,
    getAdminDriverDetail,
    updateDriverStatus,
    updateDriverCheckrStatus,
    updateDriverRating,
    getDriverAdjustments,
    createDriverAdjustment,
    reviewDriverCompliance,
};